import * as Sentry from '@sentry/react';

/**
 * Sentry error tracking configuration
 * Provides comprehensive error monitoring and debugging for production
 */

const isDevelopment = import.meta.env.DEV;
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

/**
 * Initialize Sentry error tracking
 * Call this once at application startup
 */
export const initSentry = () => {
  // Only initialize if DSN is configured and not in development
  if (!SENTRY_DSN || isDevelopment) {
    // Skipping Sentry initialization (dev mode or no DSN)
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    
    // Performance monitoring
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Sampling rates
    tracesSampleRate: 0.1, // 10% of transactions
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

    // Environment
    environment: import.meta.env.MODE,

    // Release tracking
    release: import.meta.env.VITE_APP_VERSION || 'unknown',

    // Filter out common non-critical errors
    beforeSend(event, hint) {
      const error = hint.originalException;

      // Filter out network errors that are expected
      if (error instanceof Error) {
        if (error.message.includes('Failed to fetch') || 
            error.message.includes('NetworkError')) {
          return null;
        }
      }

      return event;
    },

    // Attach additional context
    beforeBreadcrumb(breadcrumb) {
      // Filter out console.log breadcrumbs in production
      if (breadcrumb.category === 'console') {
        return null;
      }
      return breadcrumb;
    },
  });

  // Sentry initialized successfully
};

/**
 * Set user context for error tracking
 * Call this after user authentication
 */
export const setSentryUser = (user: {
  id: string;
  email?: string;
  tenantId?: string;
  role?: string;
}) => {
  if (!SENTRY_DSN || isDevelopment) return;

  Sentry.setUser({
    id: user.id,
    email: user.email,
    tenant_id: user.tenantId,
    role: user.role,
  });
};

/**
 * Clear user context on logout
 */
export const clearSentryUser = () => {
  if (!SENTRY_DSN || isDevelopment) return;
  Sentry.setUser(null);
};

/**
 * Add custom context to error reports
 */
export const setSentryContext = (
  context: string,
  data: Record<string, any>
) => {
  if (!SENTRY_DSN || isDevelopment) return;
  Sentry.setContext(context, data);
};

/**
 * Manually capture an exception
 */
export const captureException = (
  error: Error,
  context?: Record<string, any>
) => {
  if (!SENTRY_DSN || isDevelopment) {
    console.error('[Sentry] Error:', error, context);
    return;
  }

  Sentry.captureException(error, {
    contexts: context ? { custom: context } : undefined,
  });
};

/**
 * Manually capture a message
 */
export const captureMessage = (
  message: string,
  level: 'info' | 'warning' | 'error' = 'info',
  context?: Record<string, any>
) => {
  if (!SENTRY_DSN || isDevelopment) {
    console.log(`[Sentry] ${level.toUpperCase()}:`, message, context);
    return;
  }

  Sentry.captureMessage(message, {
    level,
    contexts: context ? { custom: context } : undefined,
  });
};

/**
 * Add breadcrumb for debugging
 */
export const addBreadcrumb = (
  message: string,
  data?: Record<string, any>,
  category = 'custom'
) => {
  if (!SENTRY_DSN || isDevelopment) return;

  Sentry.addBreadcrumb({
    message,
    data,
    category,
    level: 'info',
  });
};

/**
 * Track a custom span for performance monitoring
 */
export const trackSpan = async <T>(
  name: string,
  operation: () => Promise<T>
): Promise<T> => {
  if (!SENTRY_DSN || isDevelopment) {
    return operation();
  }

  return Sentry.startSpan(
    {
      name,
      op: 'custom',
    },
    operation
  );
};

// Export Sentry for direct usage when needed
export { Sentry };
